import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, AlertTriangle, Copy, Users, FileWarning, DollarSign, Activity, MessageSquare, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { format } from "@/lib/brand-tokens";
import { submissions, cards, getMemberById, getDealById, getBrandById, getCardsBySubmission } from "@/lib/mock-data";
import { useStoreVersion } from "@/hooks/useStore";

// ADM-07 — Submissions queue (exception-type buckets)
//
// Auto-approval at submission time means most submissions clear straight
// through. The queue groups what remains by *exception type* so an admin can
// triage by category instead of paging through every submission.

const FLAGGED_STATUSES = new Set(["FlaggedFormat", "FlaggedDuplicate", "FlaggedAmount", "Pending"]);

// Terminal submission statuses — admin work is done, these move out of the
// active triage view by default. Use the "Completed" tab to surface them.
const CLOSED_SUB_STATUSES = new Set(["Completed", "Rejected", "Approved"]);

// Classify a submission into a bucket based on its cards.
function classifyBucket(sub, subCards, member) {
  if (sub.status === "AwaitingMemberResponse") return "awaiting";
  if (sub.status === "Rejected")               return "completed";
  if (sub.status === "Completed")              return "completed";
  if (sub.status === "Approved")               return "completed";
  const flagged = subCards.filter(c => FLAGGED_STATUSES.has(c.status));
  if (flagged.length === 0) return "completed";
  const flagText = flagged.map(c => (c.flagReason || "").toLowerCase()).join(" | ");
  if (flagText.includes("duplicate") || flagText.includes("cross-member")) return "duplicates";
  if (flagText.includes("velocity") || flagText.includes("rate from this member")) return "velocity";
  if (flagText.includes("exceeds") || flagText.includes("face value")) return "amount";
  if (flagged.length > 0) return "format";
  const ms = member?.memberSince ? new Date(member.memberSince) : null;
  const daysSince = ms ? (Date.now() - ms.getTime()) / 86400000 : 9999;
  if (daysSince < 30 && (sub.totalFaceValue || 0) >= 1000) return "newMemberHighValue";
  return "format";
}

const BUCKETS = [
  { id: "all",                label: "Active queue",         icon: ChevronRight },
  { id: "duplicates",         label: "Duplicates",           icon: Copy,         tone: "red" },
  { id: "format",             label: "Format / pending",     icon: FileWarning,  tone: "orange" },
  { id: "amount",             label: "Amount",               icon: DollarSign,   tone: "amber" },
  { id: "velocity",           label: "Velocity",             icon: Activity,     tone: "purple" },
  { id: "newMemberHighValue", label: "New-member · high value", icon: Users,    tone: "sky" },
  { id: "awaiting",           label: "Awaiting member",      icon: MessageSquare, tone: "amber" },
  { id: "completed",          label: "Completed",            icon: ShieldCheck,  tone: "emerald" },
];

const TONE_CLASS = {
  red:     "bg-red-50 text-red-700 border-red-200",
  orange:  "bg-orange-50 text-orange-700 border-orange-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  purple:  "bg-violet-50 text-violet-700 border-violet-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  default: "bg-surface-alt text-ink border-line",
};

export default function SubmissionsQueuePage() {
  useStoreVersion();
  const [bucket, setBucket] = useState("all");
  const [q, setQ] = useState("");

  // Pre-classify every submission once per render. cards is from the store
  // facade; useStoreVersion keeps this fresh on any mutation.
  const classified = useMemo(() => {
    return submissions.map(s => {
      const subCards = cards.filter(c => c.submissionId === s.id);
      const member = getMemberById(s.memberId);
      const bucketId = classifyBucket(s, subCards, member);
      const flagged = subCards.filter(c => FLAGGED_STATUSES.has(c.status));
      const approved = subCards.filter(c => c.status === "Approved");
      const flagReasons = [...new Set(flagged.map(c => c.flagReason).filter(Boolean))];
      return { s, subCards, member, bucketId, flagged, approved, flagReasons };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions.length, cards.length]);

  const bucketCounts = useMemo(() => {
    const counts = {};
    classified.forEach(({ bucketId }) => { counts[bucketId] = (counts[bucketId] || 0) + 1; });
    // "Active queue" = everything except Completed.
    counts.all = classified.length - (counts.completed || 0);
    return counts;
  }, [classified]);

  const filtered = useMemo(() => {
    return classified
      .filter(({ bucketId }) => {
        // Default "Active queue" view excludes terminal submissions.
        if (bucket === "all") return bucketId !== "completed";
        return bucketId === bucket;
      })
      .filter(({ s, member }) => {
        if (!q) return true;
        const d = getDealById(s.dealId);
        const b = getBrandById(s.brandId);
        const hay = `${member?.firstName} ${member?.lastName} ${b?.name} ${d?.name} ${s.id}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
      .sort((a, b) => new Date(b.s.submittedAt) - new Date(a.s.submittedAt));
  }, [classified, bucket, q]);

  return (
    <div data-testid="submissions-queue-page" className="space-y-5 pb-10">
      <div>
        <div className="eyebrow">Triage</div>
        <h1 className="font-brand text-display text-ink leading-tight mt-1">Submissions</h1>
        <p className="text-body-sm text-ink-muted mt-1">
          {filtered.length} {bucket === "all" ? "in active queue" : "matching"} · clean cards auto-approve at submission · admin-reviewed submissions move to <span className="text-emerald-700 font-semibold">Completed</span> automatically when every card is resolved.
        </p>
      </div>

      {/* Bucket strip */}
      <div className="flex flex-wrap gap-2" data-testid="bucket-strip">
        {BUCKETS.map(b => {
          const count = bucketCounts[b.id] || 0;
          const active = bucket === b.id;
          const Icon = b.icon;
          const tone = b.tone ? TONE_CLASS[b.tone] : TONE_CLASS.default;
          return (
            <button
              key={b.id}
              onClick={() => setBucket(b.id)}
              data-testid={`bucket-${b.id}`}
              className={`group flex items-center gap-2 px-3.5 py-2 rounded-md border transition-all ${
                active
                  ? "bg-ink text-white border-ink shadow-sm"
                  : count > 0
                  ? `${tone} hover:shadow-sm`
                  : "bg-surface-alt text-ink-hint border-transparent opacity-60"
              }`}
            >
              <Icon className="size-3.5" />
              <span className="text-caption font-semibold">{b.label}</span>
              <span className={`text-caption tabular-nums ${active ? "text-white/80" : "opacity-70"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="-mx-8 bg-surface border-y border-line">
        <div className="px-8 py-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by member, deal, or submission ID…" className="pl-8 h-9 bg-surface-alt border-transparent focus-visible:bg-surface focus-visible:border-line" />
          </div>
          {filtered.length > 50 && (
            <span className="text-caption text-ink-subtle ml-auto">Showing latest 50 — refine via bucket or search</span>
          )}
        </div>

        {/* Sticky thead + scroll-capped body for 200+ row scale */}
        <div className="max-h-[68vh] overflow-y-auto" data-testid="submissions-table-scroll">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt sticky top-0 z-[1]">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Submission</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Submitted</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Member</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Deal · Brand</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Cards</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Face</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Exception</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.slice(0, 200).map(({ s, member, flagged, approved, flagReasons, bucketId }) => {
                const d = getDealById(s.dealId);
                const b = getBrandById(s.brandId);
                const bucketMeta = BUCKETS.find(x => x.id === bucketId) || {};
                const tone = bucketMeta.tone ? TONE_CLASS[bucketMeta.tone] : TONE_CLASS.default;
                const Icon = bucketMeta.icon || AlertTriangle;
                return (
                  <tr key={s.id} className="hover:bg-surface-alt/40" data-testid={`submission-row-${s.id}`}>
                    <td className="px-8 py-3.5">
                      <Link to={`/admin/submissions/${s.id}`} className="font-mono text-code text-brand-700 hover:text-brand-800 font-semibold">{s.id}</Link>
                      <div className="text-caption text-ink-subtle mt-0.5">
                        <span className="text-emerald-700 tabular-nums">{approved.length}</span> clean ·
                        <span className="text-orange-700 tabular-nums ml-1">{flagged.length}</span> flagged
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">{format.relative(s.submittedAt)}</td>
                    <td className="px-3 py-3.5">
                      <div className="text-ink font-medium">{member?.firstName} {member?.lastName}</div>
                      <div className="text-caption text-ink-subtle font-mono">{member?.id}</div>
                    </td>
                    <td className="px-3 py-3.5">
                      <div className="text-ink-muted truncate max-w-[200px]">{d?.name || "—"}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <BrandLogo brand={b} size="xs" />
                        <span className="text-caption text-ink-subtle">{b?.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{s.cardCount}</td>
                    <td className="px-3 py-3.5 text-ink font-semibold tabular-nums text-right">{format.currency(s.totalFaceValue)}</td>
                    <td className="px-3 py-3.5 max-w-[300px]">
                      {flagReasons.length === 0 ? (
                        <StatusBadge entity="submission" status={s.status} dot />
                      ) : (
                        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${tone}`}>
                          <Icon className="size-3" />
                          <span className="text-caption font-semibold truncate max-w-[220px]">{flagReasons[0]}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-8 py-3.5 text-right">
                      <Link to={`/admin/submissions/${s.id}`} className="inline-flex items-center gap-1 text-caption font-semibold text-brand-700 hover:text-brand-800">
                        Review <ChevronRight className="size-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-8 py-16 text-center text-ink-subtle">
                  <ShieldCheck className="size-6 text-emerald-500 mx-auto mb-2" />
                  No submissions in this bucket — your queue is clean.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
